/**
 * src/pages/WhereDidWeComeFrom/sections/CosmicExpansionCanvas.tsx
 * Signature visual for the PageHero slot:
 * Interactive canvas rendering the cosmic expansion from a singularity into
 * cosmic filaments and glowing stellar nurseries.
 * Strictly respects prefers-reduced-motion with an elegant static starry nebula.
 */

import React, { useEffect, useRef } from "react";
import { useReducedMotion } from "@/components/cosmic/useReducedMotion";
import "@/components/cosmic/cosmic.css";
export function CosmicExpansionCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const prefersReduced = useReducedMotion();
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    let animationFrameId: number;
    let width = canvas.width = canvas.parentElement?.clientWidth || 320;
    let height = canvas.height = canvas.parentElement?.clientHeight || 320;
    const isMobile = typeof window !== "undefined" && window.innerWidth < 768;
    const particleCount = isMobile ? 65 : 120;

    // Generate expansion particles
    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      radius: number;
      color: string;
      alpha: number;
      pulseSpeed: number;
      phase: number;
    }
    const colors = ["hsl(186, 100%, 55%)",
    // Electric Cyan
    "hsl(215, 100%, 65%)",
    // Laser Blue
    "hsl(270, 75%, 65%)",
    // Restrained Violet
    "hsl(0, 0%, 98%)" // White starlight
    ];
    const particles: Particle[] = [];
    const centerX = width / 2;
    const centerY = height / 2;
    for (let i = 0; i < particleCount; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = 0.3 + Math.random() * 0.9;
      const dist = Math.random() * (Math.min(width, height) * 0.42);
      particles.push({
        x: centerX + Math.cos(angle) * dist,
        y: centerY + Math.sin(angle) * dist,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        radius: 1 + Math.random() * 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        alpha: 0.3 + Math.random() * 0.7,
        pulseSpeed: 0.02 + Math.random() * 0.03,
        phase: Math.random() * Math.PI * 2
      });
    }

    // Static render for reduced-motion users
    if (prefersReduced) {
      ctx.clearRect(0, 0, width, height);

      // Central primordial glow
      const radialGlow = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, width * 0.45);
      radialGlow.addColorStop(0, "hsla(186, 100%, 50%, 0.35)");
      radialGlow.addColorStop(0.5, "hsla(270, 70%, 55%, 0.15)");
      radialGlow.addColorStop(1, "transparent");
      ctx.fillStyle = radialGlow;
      ctx.fillRect(0, 0, width, height);

      // Render static web lines
      ctx.lineWidth = 0.75;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 45) {
            ctx.strokeStyle = `hsla(186, 100%, 50%, ${0.2 * (1 - dist / 45)})`;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Render static particles
      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha;
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      return;
    }
    let time = 0;
    const render = () => {
      time += 0.015;
      ctx.clearRect(0, 0, width, height);

      // Background cosmic web glow
      const bgGrad = ctx.createRadialGradient(centerX, centerY, 10, centerX, centerY, width * 0.5);
      bgGrad.addColorStop(0, "hsla(186, 100%, 50%, 0.22)");
      bgGrad.addColorStop(0.4, "hsla(215, 100%, 60%, 0.12)");
      bgGrad.addColorStop(0.8, "hsla(270, 70%, 55%, 0.05)");
      bgGrad.addColorStop(1, "transparent");
      ctx.fillStyle = bgGrad;
      ctx.fillRect(0, 0, width, height);

      // Connect filaments between near particles
      ctx.lineWidth = 0.65;
      const maxDistance = 55;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < maxDistance) {
            const lineAlpha = (1 - dist / maxDistance) * 0.25;
            ctx.strokeStyle = `hsla(186, 100%, 50%, ${lineAlpha})`;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw and update particles
      particles.forEach(p => {
        p.x += p.vx * 0.4;
        p.y += p.vy * 0.4;

        // Wrap around boundaries
        const distFromCenter = Math.hypot(p.x - centerX, p.y - centerY);
        const maxRadius = Math.min(width, height) * 0.46;
        if (distFromCenter > maxRadius) {
          // Soft bounce inward
          p.vx = -p.vx;
          p.vy = -p.vy;
        }
        const pulse = Math.sin(time + p.phase) * 0.35 + 0.65;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius * pulse, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha * pulse;
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 8;
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      // Concentric coordinate rings
      ctx.strokeStyle = "hsla(186, 100%, 50%, 0.15)";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(centerX, centerY, width * 0.32, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = "hsla(215, 100%, 60%, 0.1)";
      ctx.beginPath();
      ctx.arc(centerX, centerY, width * 0.44, 0, Math.PI * 2);
      ctx.stroke();
      animationFrameId = requestAnimationFrame(render);
    };
    render();
    const handleResize = () => {
      if (!canvas || !canvas.parentElement) return;
      width = canvas.width = canvas.parentElement.clientWidth || 320;
      height = canvas.height = canvas.parentElement.clientHeight || 320;
    };
    window.addEventListener("resize", handleResize);
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", handleResize);
    };
  }, [prefersReduced]);
  return <div className="relative size-64 sm:size-80 lg:size-88 rounded-full border border-primary/20 bg-card/40 p-2 flex items-center justify-center overflow-hidden shadow-[0_0_30px_var(--glow-primary)]" aria-label="Interactive canvas simulation showing cosmic expansion, filament formation, and stellar ignition">
      <canvas ref={canvasRef} className="w-full h-full block rounded-full" width={320} height={320} />
      {/* Center point of origin node */}
      <div className="absolute size-3 rounded-full bg-primary shadow-[0_0_16px_var(--glow-primary)] pointer-events-none ring-2 ring-primary/40 animate-pulse" aria-hidden="true" />
      <div className="absolute bottom-3 text-[10px] font-mono text-muted-foreground/80 tracking-widest uppercase pointer-events-none">
        t &gt; 10⁻³² s Relativistic Expansion
      </div>
    </div>;
}